import React, { useState, useEffect, useRef } from 'react';
import * as api from '../../api';

const POSCashManagement = ({
  sales,
  cajaActiva,
  sesionActiva,
  onOpenDrawer,
  onCloseDrawer,
  employees
}) => {

  const [openingBalance, setOpeningBalance]         = useState('');
  const [closingBalanceInput, setClosingBalanceInput] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');

  // FIX: PIN para apertura y cierre
  const [openPin, setOpenPin]   = useState('');
  const [closePin, setClosePin] = useState('');
  const [pinError, setPinError] = useState('');

  const [cashHistory, setCashHistory]   = useState([]);
  const [liveTotalSales, setLiveTotalSales] = useState(0);
  const [refreshTick, setRefreshTick]   = useState(0);

  const lastDrawerIdRef = useRef(null);

  const loadDrawerFromAPI = async () => {
    try {
      const res = await api.getActiveCashDrawer();
      if (res?.success && res.data?.id) {
        const drawer = res.data;
        if (lastDrawerIdRef.current === drawer.id) return;
        lastDrawerIdRef.current = drawer.id;
        onOpenDrawer({
          cajaId:           drawer.id,
          empleadoId:       drawer.employee_id,
          estado:           drawer.status || "abierta",
          opening_balance:  Number(drawer.opening_balance) || 0,
          opening_datetime: drawer.opening_datetime,
          total_sales:      Number(drawer.total_sales) || 0
        });
      }
    } catch (error) {
      console.error("Error cargando caja activa:", error);
    }
  };

  const loadCashHistory = async () => {
    try {
      const res = await api.getCashDrawersHistory();
      if (res?.success && Array.isArray(res.data)) setCashHistory(res.data);
    } catch (error) {
      console.error("Error cargando historial:", error);
    }
  };

  useEffect(() => {
    loadDrawerFromAPI();
    loadCashHistory();
    const interval = setInterval(() => {
      loadDrawerFromAPI();
      loadCashHistory();
      setRefreshTick(prev => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!cajaActiva || !Array.isArray(sales)) return;
    const ventasCaja = sales.filter(
      sale => Number(sale.drawer_id) === Number(cajaActiva.cajaId)
    );
    setLiveTotalSales(
      ventasCaja.reduce((acc, sale) => acc + (Number(sale.total) || 0), 0)
    );
  }, [sales, cajaActiva, refreshTick]);

  useEffect(() => {
    if (cajaActiva) {
      setOpeningBalance(
        cajaActiva.opening_balance != null
          ? Number(cajaActiva.opening_balance).toString()
          : ''
      );
      setSelectedEmployeeId(
        cajaActiva.empleadoId != null ? String(cajaActiva.empleadoId) : ''
      );
      if (!lastDrawerIdRef.current) lastDrawerIdRef.current = cajaActiva.cajaId;
    } else {
      setOpeningBalance('');
      setSelectedEmployeeId('');
      lastDrawerIdRef.current = null;
    }
  }, [cajaActiva]);

  /* ── ABRIR CAJA ── */
  const handleOpenDrawerClick = async () => {
    setPinError('');

    if (openingBalance === '' || parseFloat(openingBalance) < 0) {
      alert('Por favor, ingresa un saldo inicial válido.');
      return;
    }
    if (!selectedEmployeeId) {
      alert('Selecciona un empleado.');
      return;
    }
    // FIX: validar PIN antes de enviar
    if (openPin.length !== 4 || !/^\d{4}$/.test(openPin)) {
      setPinError('El PIN debe ser de 4 dígitos.');
      return;
    }

    try {
      const res = await api.openCashDrawer({
        montoInicial: parseFloat(openingBalance),
        empleadoId:   Number(selectedEmployeeId),
        pin:          openPin  // FIX: enviar PIN
      });

      if (res?.success && res.data) {
        lastDrawerIdRef.current = res.data.id;
        onOpenDrawer({
          cajaId:           res.data.id,
          empleadoId:       res.data.employee_id,
          estado:           res.data.status || "abierta",
          opening_balance:  Number(res.data.opening_balance) || 0,
          opening_datetime: res.data.opening_datetime,
          total_sales:      0
        });
        setOpenPin('');
        alert("Caja abierta correctamente");
      } else {
        // FIX: mostrar error de PIN debajo del campo
        setPinError(res?.error || "Error al abrir caja");
      }
    } catch (error) {
      console.error(error);
      alert("Error de conexión.");
    }
  };

  /* ── CERRAR CAJA ── */
  const handleCloseDrawerClick = async () => {
    setPinError('');

    if (closingBalanceInput === '' || parseFloat(closingBalanceInput) < 0) {
      alert('Por favor, ingresa un saldo final válido.');
      return;
    }
    if (!cajaActiva?.cajaId) {
      alert("No hay caja activa válida.");
      return;
    }
    // FIX: validar PIN antes de enviar
    if (closePin.length !== 4 || !/^\d{4}$/.test(closePin)) {
      setPinError('El PIN debe ser de 4 dígitos.');
      return;
    }

    const montoFinal   = parseFloat(closingBalanceInput);
    const totalSales   = Number(liveTotalSales > 0 ? liveTotalSales : (cajaActiva?.total_sales || 0));
    const montoInicial = Number(cajaActiva?.opening_balance) || 0;
    const diferencia   = montoFinal - (montoInicial + totalSales);

    try {
      const res = await api.closeCashDrawer({
        cajaId:        cajaActiva.cajaId,
        montoFinal,
        ventasTotales: totalSales,
        diferencia,
        pin:           closePin  // FIX: enviar PIN
      });

      if (res?.success) {
        lastDrawerIdRef.current = null;
        setClosePin('');
        alert("Caja cerrada correctamente");
        onCloseDrawer();
      } else {
        // FIX: mostrar error de PIN debajo del campo
        setPinError(res?.error || res?.message || "Error al cerrar caja");
      }
    } catch (error) {
      console.error(error);
      alert("Error de conexión.");
    }
  };

  const calculateTotalSalesFallback = () => {
    if (!cajaActiva || !Array.isArray(sales)) return 0;
    const openingDate = new Date(cajaActiva.opening_datetime);
    return sales
      .filter(sale => new Date(sale.date || sale.created_at) >= openingDate)
      .reduce((total, sale) => total + (Number(sale.total) || 0), 0);
  };

  const totalSales = Number(
    liveTotalSales > 0 ? liveTotalSales : (cajaActiva?.total_sales ?? calculateTotalSalesFallback())
  );
  const currentBalance = (Number(cajaActiva?.opening_balance || 0) + totalSales);
  const employeeName   = Array.isArray(employees)
    ? employees.find(emp => Number(emp.id) === Number(cajaActiva?.empleadoId))?.name
    : null;

  // FIX: helper para input de PIN — solo dígitos máx 4
  const handlePinInput = (value, setter) => {
    setter(value.replace(/\D/g, '').slice(0, 4));
  };

  return (
    <>
      <div className="bg-[#F9EFD7] p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-[#3F1907] mb-4">Gestión de Caja</h3>

        {!cajaActiva || cajaActiva.estado !== 'abierta' ? (

          <div className="mb-4">
            <h4 className="text-md font-semibold text-[#3F1907] mb-3">Abrir Caja</h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-[#3F1907] text-sm font-bold mb-1">Empleado</label>
                <select
                  value={selectedEmployeeId}
                  onChange={(e) => { setSelectedEmployeeId(e.target.value); setOpenPin(''); setPinError(''); }}
                  className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
                >
                  <option value="">Selecciona Empleado</option>
                  {employees?.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[#3F1907] text-sm font-bold mb-1">Saldo Inicial</label>
                <input
                  type="number"
                  value={openingBalance}
                  onChange={(e) => setOpeningBalance(e.target.value)}
                  className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
                />
              </div>
            </div>

            {/* FIX: PIN del empleado seleccionado */}
            {selectedEmployeeId && (
              <div className="mb-4">
                <label className="block text-[#3F1907] text-sm font-bold mb-1">
                  PIN del empleado
                  <span className="text-xs font-normal text-gray-500 ml-1">
                    (confirma que está presente)
                  </span>
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  value={openPin}
                  onChange={e => handlePinInput(e.target.value, setOpenPin)}
                  placeholder="••••"
                  maxLength={4}
                  className="w-32 px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907] text-center text-lg tracking-widest"
                />
                {pinError && (
                  <p className="text-red-500 text-xs mt-1">{pinError}</p>
                )}
              </div>
            )}

            <button
              onClick={handleOpenDrawerClick}
              className="w-full bg-[#58735A] hover:bg-[#3F1907] text-[#F9EFD7] font-medium py-2 px-4 rounded-md transition duration-200"
            >
              Abrir Caja
            </button>
          </div>

        ) : (

          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-3 rounded-md border border-[#58735A]">
                <p className="text-xs text-[#3F1907]">Empleado</p>
                <p className="font-semibold text-[#3F1907]">{employeeName || 'N/A'}</p>
              </div>
              <div className="bg-white p-3 rounded-md border border-[#58735A]">
                <p className="text-xs text-[#3F1907]">Ventas</p>
                <p className="font-semibold text-[#3F1907]">${totalSales.toFixed(2)}</p>
              </div>
              <div className="bg-white p-3 rounded-md border border-[#58735A]">
                <p className="text-xs text-[#3F1907]">Saldo Esperado</p>
                <p className="font-semibold text-[#3F1907]">${currentBalance.toFixed(2)}</p>
              </div>
            </div>

            <div className="mb-3">
              <label className="block text-[#3F1907] text-sm font-bold mb-1">Saldo Final</label>
              <input
                type="number"
                value={closingBalanceInput}
                onChange={(e) => setClosingBalanceInput(e.target.value)}
                className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
              />
            </div>

            {/* FIX: PIN para cerrar caja */}
            <div className="mb-4">
              <label className="block text-[#3F1907] text-sm font-bold mb-1">
                PIN de {employeeName || 'quien abrió la caja'}
                <span className="text-xs font-normal text-gray-500 ml-1">
                  (solo quien la abrió puede cerrarla)
                </span>
              </label>
              <input
                type="password"
                inputMode="numeric"
                value={closePin}
                onChange={e => handlePinInput(e.target.value, setClosePin)}
                placeholder="••••"
                maxLength={4}
                className="w-32 px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907] text-center text-lg tracking-widest"
              />
              {pinError && (
                <p className="text-red-500 text-xs mt-1">{pinError}</p>
              )}
            </div>

            <button
              onClick={handleCloseDrawerClick}
              className="w-full bg-red-600 hover:bg-red-700 text-[#F9EFD7] font-medium py-2 px-4 rounded-md transition duration-200"
            >
              Cerrar Caja
            </button>
          </div>
        )}
      </div>

      {/* BITÁCORA */}
      <div className="bg-[#F9EFD7] mt-6 p-4 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-[#3F1907] mb-3">Bitácora de Cajas</h3>

        {cashHistory.length === 0 ? (
          <p className="text-[#3F1907]">No hay registros.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#58735A]">
              <thead className="bg-[#58735A]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Empleado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Apertura</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Cierre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Inicial</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Ventas</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Final</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#F9EFD7] uppercase">Diferencia</th>
                </tr>
              </thead>
              <tbody className="bg-[#F9EFD7] divide-y divide-[#58735A]">
                {cashHistory.map(caja => (
                  <tr key={caja.id}>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">{caja.employee_name}</td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      {new Date(caja.opening_datetime).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      {caja.closing_datetime
                        ? new Date(caja.closing_datetime).toLocaleString()
                        : 'Abierta'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      ${Number(caja.opening_balance).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      ${Number(caja.total_sales).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      ${Number(caja.closing_balance || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#3F1907]">
                      ${Number(caja.difference || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default POSCashManagement;